import React, { useState } from 'react';
import { ArrowRightIcon, CheckCircle2Icon, CreditCardIcon, Loader2Icon } from 'lucide-react';
import { PractitionerFlowShell } from '../components/practitioners/PractitionerFlowShell';
import { AppDownloadChooser } from '../components/practitioners/AppDownloadChooser';
import { ConfirmationStatusPanel } from '../components/practitioners/ConfirmationStatusPanel';
import { useMembershipConfirmation, type ConfirmationStatus } from '../hooks/useMembershipConfirmation';
import { welcomeNextSteps } from '../data/practitionerWelcome';
import { foundingPlans } from '../data/foundingOffer';
import { useScreenInit } from '../useScreenInit.js';
import { describeError, getStoredPractitionerSession, redirectToPractitionerConnect, startPractitionerConnectOnboarding } from '../utils/practitionerApi';

/**
 * /practitioners/welcome
 *
 * Stripe membership Checkout success page.
 *
 * Membership confirmation and Stripe Connect are intentionally
 * separate:
 *
 * 1. Stripe membership Checkout
 * 2. Backend confirms FemEntity membership
 * 3. Practitioner starts Stripe Connect payout onboarding
 * 4. Stripe hosts the bank/business/identity setup
 * 5. Stripe returns to this page
 * 6. Practitioner continues to the FemEntity app
 *
 * The Stripe membership redirect alone is never treated as proof
 * of a subscription.
 */
export function PractitionerWelcomePage() {
  /**
   * Design-canvas preview only.
   *
   * Honoured when rendered inside the Magic Patterns editor's
   * Screens iframe with ?mp_screen=…
   */
  const screen = useScreenInit() as {
    confirmationPreview?: ConfirmationStatus;
  };
  const preview = window.self !== window.top ? screen.confirmationPreview : undefined;
  const {
    status,
    planId,
    trialEndDate,
    retry
  } = useMembershipConfirmation(preview);
  const [startingConnect, setStartingConnect] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const params = new URLSearchParams(window.location.search);

  /**
   * Stripe Connect returns here using:
   *
   * /practitioners/welcome?connect=return
   *
   * A return redirect does NOT by itself prove that all Stripe
   * requirements were completed. Proper Connect status
   * verification will be handled separately.
   */
  const returnedFromConnect = params.get('connect') === 'return';
  const connectRefresh = params.get('connect') === 'refresh';

  /**
   * Start Stripe-hosted Connect onboarding.
   */
  async function handleSetUpPayments() {
    if (startingConnect) {
      return;
    }
    setConnectError(null);
    const session = getStoredPractitionerSession();
    if (!session) {
      setConnectError('Your secure session has expired. Please sign in again before setting up payments.');
      return;
    }
    try {
      setStartingConnect(true);
      const result = await startPractitionerConnectOnboarding(session);
      redirectToPractitionerConnect(result.onboardingUrl);
    } catch (error) {
      setConnectError(describeError(error));
      setStartingConnect(false);
    }
  }

  /**
   * Membership must be confirmed before we expose
   * practitioner payout setup.
   */
  if (status !== 'confirmed') {
    return <PractitionerFlowShell currentStep={1} data-id="element-804" data-mp="bao6k33jjvyu">
        <ConfirmationStatusPanel status={status} onRetry={retry} data-id="element-805" data-mp="3jp6jai4ffzos" />
      </PractitionerFlowShell>;
  }
  const planName = foundingPlans.find(plan => plan.id === planId)?.name;
  const complimentaryUntil = formatDate(trialEndDate);
  return <PractitionerFlowShell currentStep={2} data-id="element-806" data-mp="3omyk61n1uw54">
      <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-807" data-mp="3opb1qdowb4hn">
        {/* Membership confirmation */}
        <section className="rounded-[2rem] bg-purple-800 px-7 py-14 md:px-16 md:py-20 text-center" data-id="element-808" data-mp="18y6pg6qx2egk">
          <p className="font-serif italic text-lg text-purple-200" data-id="element-809" data-mp="13z3kemaxs88l">
            {planName ? `${planName} · Founding Member` : 'Founding Member'}
          </p>

          <h1 className="mt-4 font-serif text-4xl md:text-6xl text-white leading-[1.05] tracking-tight" data-id="element-810" data-mp="3ekmk7whgjkoa">
            Welcome, Founding Member 💗
          </h1>

          <p className="mt-6 font-serif text-xl md:text-2xl text-white" data-id="element-811" data-mp="3nlawomr7dcz2">
            Your Fem Entity membership is confirmed.
          </p>

          <p className="mt-5 max-w-2xl mx-auto text-base md:text-lg text-purple-100 font-light leading-relaxed" data-id="element-812" data-mp="3sjvpi144kudh">
            Welcome to Canada’s national women’s health
            network. Your first two months are complimentary,
            and you’re now ready to continue setting up your
            practitioner presence on Fem Entity.
          </p>

          <dl className="mt-12 mx-auto max-w-3xl grid gap-6 border-t border-white/15 pt-8 sm:grid-cols-3 text-center" data-id="element-813" data-mp="f60ocwwpzbiu">
            <div data-id="element-814" data-mp="k53w4p433vui">
              <dt className="text-sm text-purple-200" data-id="element-815" data-mp="k4lnsu45dr68">
                Membership
              </dt>

              <dd className="mt-1 font-serif text-xl text-white" data-id="element-816" data-mp="u9kafe1x14w7">
                {planName ?? 'Confirmed'}
              </dd>
            </div>

            <div data-id="element-817" data-mp="k9jkvadgdsjk">
              <dt className="text-sm text-purple-200" data-id="element-818" data-mp="kb4hd5jbvlk2">
                Months 1–2
              </dt>

              <dd className="mt-1 font-serif text-xl text-white" data-id="element-819" data-mp="g9j7zu605pem">
                {complimentaryUntil ? `Free until ${complimentaryUntil}` : 'Complimentary'}
              </dd>
            </div>

            <div data-id="element-820" data-mp="180fiwp8i5jz9">
              <dt className="text-sm text-purple-200" data-id="element-821" data-mp="18207ly84y8p7">
                Founding rate
              </dt>

              <dd className="mt-1 font-serif text-xl text-white" data-id="element-822" data-mp="z7byfwzfw6v4">
                Locked in while active
              </dd>
            </div>
          </dl>
        </section>

        {returnedFromConnect ? (
      /**
       * Practitioner has returned from Stripe Connect.
       *
       * We deliberately do NOT say "payouts enabled"
       * here yet because the Stripe account still needs
       * to be verified server-side.
       */
      <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-823" data-mp="3q4oszxmctt12">
            <div className="flex items-start gap-4" data-id="element-824" data-mp="3gfne6arjphoo">
              <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-700 text-white" data-id="element-825" data-mp="3ge2l6pkvq6sx">
                <CheckCircle2Icon aria-hidden="true" className="h-5 w-5" data-id="element-826" data-mp="32hys4zlqs4i1" />
              </div>

              <div data-id="element-827" data-mp="2sihsozx32mui">
                <p className="text-sm font-semibold text-purple-700" data-id="element-828" data-mp="2sizsg6iyfj76">
                  Payment setup
                </p>

                <h2 className="mt-2 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-829" data-mp="2xivonjleu6c6">
                  You’re back from Stripe
                </h2>

                <p className="mt-4 max-w-2xl text-base md:text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-830" data-mp="vod8ai3qkv2v">
                  Your payment information has been submitted
                  to Stripe. You can now continue to the Fem
                  Entity app to complete your practitioner
                  profile and verification.
                </p>
              </div>
            </div>
          </section>) : (
      /**
       * Required Connect onboarding step.
       */
      <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-831" data-mp="2ywwypyzvl334">
            <div className="max-w-3xl" data-id="element-832" data-mp="2uyftjl00m2oc">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-700 text-white" data-id="element-833" data-mp="38wnyog77gaal">
                <CreditCardIcon aria-hidden="true" className="h-5 w-5" data-id="element-834" data-mp="33wrtidmructg" />
              </div>

              <p className="mt-7 text-sm font-semibold text-purple-700" data-id="element-835" data-mp="1ek7j2osirjt2">
                One more setup step
              </p>

              <h2 className="mt-2 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-836" data-mp="nq4drtxksjof">
                Set up client payments
              </h2>

              <p className="mt-5 max-w-2xl text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-837" data-mp="sncvjtetalp5">
                Connect your payout account securely through
                Stripe so you can receive payments from clients
                who book with you through Fem Entity.
              </p>

              <p className="mt-4 max-w-2xl text-sm text-charcoal/60 leading-relaxed" data-id="element-838" data-mp="4sjq2q6jrr5e">
                Stripe will securely collect the business,
                identity, and banking information required for
                payouts. Fem Entity does not collect your bank
                account information on this website.
              </p>

              {connectRefresh && <div className="mt-6 rounded-xl border border-purple-200 bg-white px-5 py-4 text-sm text-charcoal/70" data-id="element-839" data-mp="1q3a2kehznu9b">
                  Your previous Stripe setup session expired or
                  needs to be reopened. Select the button below
                  to continue.
                </div>}

              {connectError && <div role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700" data-id="element-840" data-mp="3aoqatmc44m6q">
                  {connectError}
                </div>}

              <button type="button" onClick={handleSetUpPayments} disabled={startingConnect} className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-purple-700 px-7 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2" data-id="element-841" data-mp="2vyj3a1nn8nkt">
                {startingConnect ? <>
                    <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-842" data-mp="2c4zu4nbcwqce" />
                    Opening Stripe…
                  </> : <>
                    Set up payments with Stripe
                    <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-843" data-mp="2h42sjowtzybe" />
                  </>}
              </button>
            </div>
          </section>)}

        {/* App next step */}
        <section aria-labelledby="next-heading" className="mt-20 md:mt-24 grid gap-12 lg:grid-cols-[minmax(0,1fr)_26rem] lg:gap-16 lg:items-start" data-id="element-844" data-mp="227bmwxwkdqdc">
          <div data-id="element-845" data-mp="1xeiikb50ck1s">
            <h2 id="next-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-846" data-mp="19r0zd3zt6uaf">
              Next: Download the Fem Entity app
            </h2>

            <p className="mt-5 max-w-xl text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-847" data-mp="vls8pcxt7088">
              Download the app and sign in using the same Fem
              Entity account you just created. From there,
              you’ll complete your practitioner profile, submit
              your credentials for verification, and get ready
              to connect with clients.
            </p>

            <h3 className="mt-12 text-sm font-semibold text-purple-700" data-id="element-848" data-mp="1ec9pqk9a47dr">
              In the app, you’ll
            </h3>

            <ol className="mt-6 border-l border-purple-200" data-id="element-849" data-mp="1jbcs1wxvjzi1">
              {welcomeNextSteps.map((step, index) => <li key={step.title} className="relative pl-10 pb-8 last:pb-0" data-id="element-850" data-mp="14dkdp842dh5f" data-mpi={index}>
                    <span aria-hidden="true" className="absolute -left-[14px] top-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-purple-700 text-xs font-semibold text-white" data-id="element-851" data-mp="3p3jmfjsxnu93">
                      {index + 1}
                    </span>

                    <h4 className="font-serif text-xl text-charcoal leading-snug" data-id="element-852" data-mp="1vl2kpqwvue1d">
                      {step.title}
                    </h4>

                    <p className="mt-1.5 text-base text-charcoal/70 leading-relaxed" data-id="element-853" data-mp="1qsj4snaq36te">
                      {step.description}
                    </p>
                  </li>)}
            </ol>
          </div>

          <div className="lg:sticky lg:top-32" data-id="element-854" data-mp="1cubcwtsjmvp3">
            <AppDownloadChooser data-id="element-855" data-mp="1ctjfddfdynw6" />
          </div>
        </section>

        <p className="mt-20 text-center font-serif text-3xl md:text-4xl text-purple-800" data-id="element-856" data-mp="17uyhvlriqibf">
          See you inside. 💗
        </p>
      </div>
    </PractitionerFlowShell>;
}
function formatDate(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-CA', {
    month: 'long',
    day: 'numeric'
  });
}